import { Text, UnstyledButton } from '@mantine/core';
import type { ReactNode } from 'react';

interface ChipButtonProps {
  label: string;
  /** Signal-colored border/text when true (the default — used for single
   * actionable chips like "change password" or "download CSV"), ink-colored
   * when false (used for the inactive option in a multi-choice toggle like
   * the language picker, or for de-emphasized secondary actions like a
   * modal's "Cancel" button next to a primary filled submit button). */
  active?: boolean;
  disabled?: boolean;
  /** Optional leading icon, rendered at the same signal/ink tint as the label. */
  icon?: ReactNode;
  /** Stretches to fill its container (e.g. inside a Mantine `Group grow` row
   * next to a primary button), centering its label/icon instead of the
   * default content-hugging width. */
  fullWidth?: boolean;
  onClick: () => void;
}

/**
 * Shared bordered "chip" button style used across the app for compact,
 * mono-labeled actions/toggles (language picker, change-password, CSV
 * export, ...) — a bordered box with a mono/bold label, rather than a
 * filled Mantine `Button`.
 */
export const ChipButton = ({
  label,
  active = true,
  disabled,
  icon,
  fullWidth,
  onClick,
}: ChipButtonProps) => (
  <UnstyledButton
    disabled={disabled}
    onClick={onClick}
    style={{
      alignItems: 'center',
      border: `2px solid ${active ? 'var(--mantine-color-signal-6)' : 'var(--mantine-color-ink-3)'}`,
      cursor: disabled ? 'not-allowed' : 'pointer',
      display: 'inline-flex',
      gap: icon ? 8 : 0,
      justifyContent: 'center',
      opacity: disabled ? 0.5 : 1,
      padding: '10px 14px',
      width: fullWidth ? '100%' : undefined,
    }}
  >
    {icon}
    <Text c={active ? 'signal.7' : 'ink.8'} ff="monospace" fw={700} size="xs">
      {label}
    </Text>
  </UnstyledButton>
);
